'use client'

import { useMemo, useState } from 'react'

import { LayoutDashboardIcon, ListIcon, SearchIcon } from 'lucide-react'

import { SECTION_TITLE } from '@/lib/typography'

import { CatalogProductCard } from '@/components/blocks/catalog-product-card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Reveal } from '@/components/ui/reveal'
import { useDictionary } from '@/lib/i18n/provider'
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { cn } from '@/lib/utils'
import type { BrandProduct } from '@/types/website'

const ALL = 'all'

/**
 * Барааны жагсаалт — theme-ийн shop toolbar-ын бүтцийг (тоо, эрэмбэ,
 * сүлжээ/жагсаалт солих) дагасан ч каталогид тохируулсан: ангиллаар
 * шүүх, нэрээр хайх. Үнэ, сагс байхгүй.
 */
export const CatalogProducts = ({ products, total }: { products: BrandProduct[]; total: number }) => {
  const t = useDictionary()

  const [view, setView] = useState<'grid' | 'list'>('grid')
  const [category, setCategory] = useState(ALL)
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState<'name-asc' | 'name-desc'>('name-asc')

  const categories = useMemo(
    () => [...new Set(products.map(p => p.category).filter(Boolean))].sort() as string[],
    [products]
  )

  const visible = useMemo(() => {
    const query = search.trim().toLowerCase()

    return products
      .filter(p => (category === ALL ? true : p.category === category))
      .filter(p =>
        query ? [p.name, p.internal_code, p.barcode].some(value => (value ?? '').toLowerCase().includes(query)) : true
      )
      .sort((a, b) => {
        const compared = (a.name ?? '').localeCompare(b.name ?? '')

        return sort === 'name-asc' ? compared : -compared
      })
  }, [products, category, search, sort])

  return (
    <section className='mt-16 border-t pt-12'>
      <div className='flex flex-wrap items-end justify-between gap-3'>
        <h2 className={SECTION_TITLE}>{t.products.heading}</h2>
        <span className='text-muted-foreground text-xl'>
          {visible.length === total
            ? `${total} ${t.products.countSuffix}`
            : `${visible.length} / ${total} ${t.products.countSuffix}`}
        </span>
      </div>

      {/* Toolbar */}
      <div className='mt-6 flex flex-wrap items-center gap-3'>
        <div className='relative min-w-56 flex-1'>
          <SearchIcon className='text-muted-foreground absolute start-3 top-1/2 size-4 -translate-y-1/2' />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={t.products.searchPlaceholder}
            className='ps-9'
          />
        </div>

        {categories.length > 1 && (
          <Select value={category} onValueChange={value => setCategory(value ?? ALL)}>
            <SelectTrigger className='sm:w-56'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value={ALL}>{t.products.allCategories}</SelectItem>
                {categories.map(item => (
                  <SelectItem key={item} value={item}>
                    {item}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        )}

        <Select value={sort} onValueChange={value => setSort((value ?? 'name-asc') as typeof sort)}>
          <SelectTrigger className='sm:w-44'>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectItem value='name-asc'>{t.products.sortNameAsc}</SelectItem>
              <SelectItem value='name-desc'>{t.products.sortNameDesc}</SelectItem>
            </SelectGroup>
          </SelectContent>
        </Select>

        <div className='flex items-center gap-1'>
          <Button
            variant={view === 'grid' ? 'default' : 'outline'}
            size='icon'
            aria-label={t.products.grid}
            onClick={() => setView('grid')}
          >
            <LayoutDashboardIcon className='size-4' />
          </Button>
          <Button
            variant={view === 'list' ? 'default' : 'outline'}
            size='icon'
            aria-label={t.products.list}
            onClick={() => setView('list')}
          >
            <ListIcon className='size-4' />
          </Button>
        </div>
      </div>

      {visible.length === 0 ? (
        <p className='text-muted-foreground mt-10 rounded-lg border border-dashed p-12 text-center'>
          {t.products.empty}
        </p>
      ) : (
        <div
          className={cn(
            'mt-8',
            view === 'grid' ? 'grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' : 'flex flex-col gap-3'
          )}
        >
          {visible.map((product, index) => (
            <Reveal key={product.id} index={index} columns={view === 'grid' ? 4 : 1} className='h-full'>
              <CatalogProductCard product={product} view={view} />
            </Reveal>
          ))}
        </div>
      )}
    </section>
  )
}
